/**
 * Chunk stale recovery — detects 404'd code-split chunks after a redeploy
 * and prompts the user to reload instead of showing a white screen.
 *
 * Vite code-splits into hashed chunks. After a deploy, old chunk filenames
 * no longer exist on the server. Dynamic imports fail with a network error
 * or 404. This module catches those errors and shows a reload banner.
 */

let installed = false;
let bannerShown = false;

/**
 * Show a reload prompt banner at the top of the viewport.
 */
function showReloadBanner(): void {
  if (bannerShown) return;
  bannerShown = true;

  const banner = document.createElement('div');
  banner.style.cssText = `
    position: fixed; top: 0; left: 0; right: 0; z-index: 99999;
    background: var(--bg-secondary, #1a1a2e);
    border-bottom: 2px solid var(--accent-amber, #f59e0b);
    padding: 12px 20px;
    display: flex; align-items: center; justify-content: center; gap: 12px;
    font-family: var(--font-sans, system-ui, sans-serif);
    color: var(--text-primary, #e0e0e0);
    font-size: 13px;
    animation: slideDown 300ms ease;
  `;

  const msg = document.createElement('span');
  msg.textContent = 'A new version of Sentinel is available.';
  banner.appendChild(msg);

  const btn = document.createElement('button');
  btn.style.cssText = `
    background: var(--accent-amber, #f59e0b);
    color: #000; border: none; border-radius: 4px;
    padding: 6px 16px; font-size: 12px; font-weight: 600;
    cursor: pointer;
  `;
  btn.textContent = 'Reload';
  btn.onclick = () => window.location.reload();
  banner.appendChild(btn);

  const dismiss = document.createElement('button');
  dismiss.style.cssText = `
    background: none; border: none; color: var(--text-muted, #666);
    cursor: pointer; font-size: 16px; padding: 0 4px;
  `;
  dismiss.textContent = '\u00D7';
  dismiss.title = 'Dismiss';
  dismiss.onclick = () => banner.remove();
  banner.appendChild(dismiss);

  document.body.appendChild(banner);
}

/**
 * Check if an error looks like a stale chunk load failure.
 * Vite dynamic imports fail with TypeError or specific error messages.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const message = error instanceof Error
    ? error.message
    : String(error);

  // Common patterns for chunk load failures
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    // Network errors during dynamic import
    (message.includes('fetch') && message.includes('failed'))
  );
}

/**
 * Install chunk reload detection.
 * Hooks into window error and unhandledrejection events.
 * Returns a cleanup function.
 */
export function initChunkReloadHandler(): () => void {
  if (installed) return () => {};
  installed = true;

  const errorHandler = (event: ErrorEvent): void => {
    if (isChunkLoadError(event.error)) {
      event.preventDefault();
      showReloadBanner();
    }
  };

  const rejectionHandler = (event: PromiseRejectionEvent): void => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      showReloadBanner();
    }
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    installed = false;
    bannerShown = false;
  };
}
